import { useCallback, useEffect, useState } from 'react';

import { errorKey, type AuthApi, type PayoutIdentity } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { feeOf, type GameApi, type WithdrawState } from './api';

/**
 * 提现面板。两个阶段，**按后端说的走**：
 *
 *   没绑收款身份   → 绑定表单（一个账号一次，绑完不能自己改）
 *   绑好了         → 提现表单 + 额度 + 提现记录
 *
 * 数据来自两个服务，各拉各的：
 *   account       收款身份（CPF —— PII 只有它碰，而且只下发打码的号）
 *   game-server   余额、额度、打码量、提现记录（钱包的单写者）
 *
 * 用 allSettled：**哪边挂了就少那一块**，不是整个面板空白。
 *
 * 三条规则在这个界面上的样子（三条都是后端保证的，这里只是如实画出来）：
 *   一个账号一个 CPF   绑好之后表单消失，换成只读卡
 *   不能自己解绑       **没有解绑按钮** —— 后端也没有那条路由
 *   CPF 全服唯一       撞了显示"已被使用"，**不说被谁占用**
 */
export function WithdrawSheet(
  { open, api, game, token, onClose, money, onDone }:
  {
    open: boolean;
    api: AuthApi;
    game: GameApi;
    token: string;
    onClose(): void;
    money(n: number): string;
    /** 提现成功后通知外面刷新余额 —— 钱已经扣了，个人页上那个数不能还是旧的 */
    onDone(): void;
  },
) {
  const { t, i18n } = useI18n();
  const [ident, setIdent] = useState<PayoutIdentity | null>(null);
  const [wd, setWd] = useState<WithdrawState | null>(null);
  const [cpf, setCpf] = useState('');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  // **每次提交用一个新的请求号。** 响应丢了用户再点，带的是同一个号，
  // 后端就知道那是重发而不是第二笔。成功之后才换新的。
  const [reqId, setReqId] = useState(newReqId);

  // **手续费在他填数字的时候就要看得见。** 提交之后才告诉他"实际到账 98"，
  // 那叫套路 —— 而且他会把它当成扣错了钱来投诉。
  //
  // 这里是**预览**：真正收多少以提交时服务端算的为准（响应里带 fee）。
  // 规则由服务端下发，公式抄了一份（`feeOf`）—— 和 totalBet 同一个处理：
  // 要在输入过程中实时显示，只能算在本地。
  const n = Number(amount);
  const fee = Number.isFinite(n) && n > 0 ? feeOf(wd?.fee, n) : 0;
  const net = Math.max(0, Math.round((n - fee) * 100) / 100);

  const load = useCallback(async () => {
    const [a, g] = await Promise.allSettled([
      api.payoutIdentity(token), game.withdrawals(token)]);
    if (a.status === 'fulfilled') setIdent(a.value);
    if (g.status === 'fulfilled') setWd(g.value);
    const bad = [a, g].find((r) => r.status === 'rejected');
    setErr(bad ? t(errorKey((bad as PromiseRejectedResult).reason)) : '');
  }, [api, game, token, t]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  async function bind(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(''); setOk('');
    try {
      setIdent(await api.bindPayoutIdentity(token, cpf, name));
      setCpf(''); setName('');
    } catch (e2) {
      setErr(t(errorKey(e2)));
    } finally { setBusy(false); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(''); setOk('');
    try {
      // **回执里的 fee / net 才是权威的**，上面那个预览只是给人看的。
      // 两者对不上时（比如他在填数字的中途升了级），以这个为准。
      const r = await game.withdraw(token, reqId, Number(amount));
      setAmount('');
      setReqId(newReqId());       // 这一笔成了，下一笔换新号
      setOk(r.fee > 0
        ? t('withdraw.submittedFee', { v: money(r.net), f: money(r.fee) })
        : t('withdraw.submitted'));
      await load();
      onDone();                   // 钱已经扣了，外面那个余额得跟上
    } catch (e2) {
      setErr(t(errorKey(e2)));
      // **请求号不换。** 这一笔到底有没有落地我们不知道（可能是响应丢了），
      // 换了号再点就会变成第二笔。
    } finally { setBusy(false); }
  }

  const bound = ident?.bound === true && ident.identity !== null;
  // 提现按钮什么时候能点：绑好了、打码量达标、今日还有额度、余额够最低额
  const blocked = !wd ? 'loading'
    : wd.wagerLeft > 0 ? 'wager'
    : wd.dailyLeft <= 0 ? 'quota'
    : wd.maxNow < wd.minWithdraw ? 'tooSmall'
    : '';

  return (
    <Sheet open={open} title={t('withdraw.title')} onClose={onClose}>
      <div className="stack">
        <div className="card spread">
          <span className="small dim">{t('withdraw.available')}</span>
          <span className="h2 num">{wd ? money(wd.bal) : '—'}</span>
        </div>

        {/* ---- 收款信息 ---- */}
        <h3 className="h2 wd__h">{t('withdraw.payoutTo')}</h3>
        {ident === null && !err && <p className="small dim">{t('action.working')}</p>}

        {bound && (
          /* 绑好之后**没有编辑入口，也没有解绑按钮** —— 后端也没有那条路由。
             界面上藏起来但接口开着，等于没藏；反过来后端挡住而界面留着入口，
             用户就会点了之后收到一个看不懂的错误。 */
          <div className="card stack wd__bound">
            <div className="spread">
              <span className="num wd__cpf">{ident!.identity!.masked}</span>
              <span className="wd__tag">{t('withdraw.bound')}</span>
            </div>
            <span className="small">{ident!.identity!.holderName}</span>
            <span className="small dim">{t('withdraw.cannotUnbind')}</span>
          </div>
        )}

        {ident !== null && !bound && (
          <form className="stack" onSubmit={bind}>
            <p className="small dim">{t('withdraw.bindFirst')}</p>
            <label className="field">
              <span className="field__label">{t('withdraw.cpf')}</span>
              {/* **不在前端算校验位** —— 后端已经算了，两份实现迟早不一致，
                  而"前端说对、后端说错"是最难解释给用户听的一种状态。 */}
              <input className="field__input num" inputMode="numeric" autoComplete="off"
                     placeholder="123.456.789-09" value={cpf} maxLength={14} required
                     onChange={(e) => setCpf(e.target.value)} />
              <span className="field__hint">
                {t('withdraw.cpfHint', { example: '123.456.789-09' })}
              </span>
            </label>
            <label className="field">
              <span className="field__label">{t('withdraw.holderName')}</span>
              <input className="field__input" autoComplete="name" value={name}
                     maxLength={128} required onChange={(e) => setName(e.target.value)} />
              <span className="field__hint">{t('withdraw.holderNameHint')}</span>
            </label>
            {/* 绑之前把"只有一次机会"说在前面。绑完再说就太晚了。 */}
            <p className="small dim">{t('withdraw.oncePerAccount')}</p>
            <Button variant="primary" type="submit" disabled={busy} className="btn--wide">
              {busy ? t('action.working') : t('withdraw.bind')}
            </Button>
          </form>
        )}

        {/* ---- 提现表单。**绑好了才出现** ---- */}
        {bound && wd && (
          <>
            <div className="card spread">
              <span className="small dim">
                {t('withdraw.dailyLimit')} · {money(wd.dailyLimit)}
              </span>
              <span className="small num">
                {t('withdraw.dailyLeft', { v: money(wd.dailyLeft) })}
              </span>
            </div>
            <p className="small dim">
              {t('withdraw.vipTip', { n: wd.vipLevel, v: money(wd.dailyLimit) })}
            </p>

            {/* 提不了的时候**说清楚是哪一条**，别只给一个灰掉的按钮 */}
            {blocked === 'wager' && (
              <p className="field__err" role="status">
                {t('withdraw.wagerBlocked', { v: money(wd.wagerLeft) })}
              </p>
            )}
            {blocked === 'quota' && (
              <p className="field__err" role="status">{t('withdraw.noQuotaLeft')}</p>
            )}

            <form className="stack" onSubmit={submit}>
              <label className="field">
                <span className="field__label">{t('withdraw.amount')}</span>
                <div className="wd__amount">
                  {/* type=text + inputMode=decimal：type=number 在移动端会
                      吃掉逗号小数点、还会被滚轮改值。金额不能靠运气。 */}
                  <input className="field__input num" type="text" inputMode="decimal"
                         value={amount} required disabled={!!blocked}
                         onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} />
                  {/* "全部"填的是 maxNow —— **服务端算好的**（余额和额度取小者）。
                      前端自己算的话，算错了就是让人提交一个必然被拒的数字。 */}
                  <Button variant="ghost" type="button" disabled={!!blocked}
                          onClick={() => setAmount(String(wd.maxNow))}>
                    {t('withdraw.all')}
                  </Button>
                </div>
                {/* 提不了的时候不显示区间 —— 上面已经说了原因，而
                    "R$20.00 ~ R$0.00" 是一个不成立的区间，只会让人困惑。 */}
                {!blocked && (
                  <span className="field__hint">
                    {t('withdraw.amountHint',
                       { min: money(wd.minWithdraw), max: money(wd.maxNow) })}
                  </span>
                )}
              </label>

              {/* ---- 手续费与实际到账 ----
                  **只在填了数字之后才出现。** 空着的时候显示
                  "手续费 R$0.00 / 到账 R$0.00" 是三行没有信息的噪声。 */}
              {!blocked && n > 0 && (
                <div className="card stack wd__fee">
                  <div className="spread">
                    <span className="small dim">{t('withdraw.fee')}</span>
                    <span className="small num">
                      {fee > 0 ? `− ${money(fee)}` : t('withdraw.feeFree')}
                    </span>
                  </div>
                  <div className="spread">
                    {/* **到账是最重要的那个数**，所以它是唯一加粗的一行 */}
                    <span className="small">{t('withdraw.net')}</span>
                    <span className="num wd__net">{money(net)}</span>
                  </div>
                </div>
              )}
              {/* "再升一级就免手续费"是玩家算得清的权益，值得说 */}
              {!blocked && wd.nextFeeFree !== null && (
                <p className="small dim">
                  {t('withdraw.feeFreeAt', { n: wd.nextFeeFree })}
                </p>
              )}
              <Button variant="primary" type="submit" className="btn--wide"
                      disabled={busy || !!blocked}>
                {busy ? t('action.working') : t('withdraw.submit')}
              </Button>
            </form>

            {/* ---- 提现记录 ---- */}
            <h3 className="h2 wd__h">{t('withdraw.history')}</h3>
            {wd.items.length === 0
              ? <p className="small dim">{t('withdraw.noHistory')}</p>
              : (
                <ul className="wd__list">
                  {wd.items.map((it) => (
                    <li key={it.id} className="wd__item">
                      <div className="spread">
                        <span className="num">{money(it.amount)}</span>
                        <span className={`wd__st wd__st--${it.status}`}>
                          {t(`withdraw.st.${it.status}` as 'withdraw.st.pending')}
                        </span>
                      </div>
                      <span className="small dim">
                        {it.ts ? i18n.dateTime(it.ts * 1000) : ''}
                      </span>
                      {/* 历史单的手续费是**当时**那个数，服务端给的，
                          前端不拿现在的费率去减 */}
                      {it.fee > 0 && (
                        <span className="small dim">
                          {t('withdraw.itemFee',
                             { f: money(it.fee), v: money(it.net) })}
                        </span>
                      )}
                      {/* 驳回了要说清楚**钱回来了** —— 不说的话玩家会以为钱没了 */}
                      {it.status === 'rejected' && (
                        <span className="small dim">
                          {t('withdraw.refunded')}{it.note ? ` · ${it.note}` : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
          </>
        )}

        {ok && <p className="wd__ok" role="status">{ok}</p>}
        {err && <p className="field__err" role="alert">{err}</p>}
      </div>
    </Sheet>
  );
}

/** 请求号。`crypto.randomUUID` 在所有目标浏览器上都有（安全上下文里）。 */
function newReqId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // 老浏览器 / 非安全上下文。**不是加密用途**，只要够独特就行。
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}
